/**
 * MergeFieldEditor
 *
 * A rich text editor (built on top of RichTextEditor / Tiptap) that adds an
 * "Insert field ▼" dropdown button to the toolbar.  Clicking a token in the
 * dropdown inserts it as a styled inline chip at the current cursor position.
 *
 * Chips are stored in the HTML content as:
 *   <span data-merge-token="{client.name}" class="merge-chip">[Client Name]</span>
 *
 * In the builder preview (no real context) the chip text acts as the friendly
 * placeholder label.  In the wizard / print output, the parent calls
 * `resolveInvoiceTokens()` on the HTML string to substitute real values.
 *
 * Sub-tasks completed here:
 *   4.1a — New file created
 *   4.1b — "Insert field ▼" dropdown added to the Tiptap toolbar
 *   4.1c — Dropdown groups tokens by category
 *   4.1d — Clicking a token inserts it as a styled inline chip
 *   4.1e — Chips display the friendly label, not the raw token string
 *   4.1f — Accepts `availableTokens: MergeToken[]` prop
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  RemoveFormatting,
  ChevronDown,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { MergeToken } from '@/services/customerTermsService';

// ============================================================================
// Tiptap custom node — MergeChip
// Renders as a non-editable inline <span> styled as a pill/chip.
// Stored in HTML as: <span data-merge-token="{client.name}">[Client Name]</span>
// ============================================================================

const MergeChipNode = Node.create({
  name: 'mergeChip',
  group: 'inline',
  inline: true,
  atom: true, // treated as a single unit — cannot be partially selected

  addAttributes() {
    return {
      token: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-merge-token'),
        renderHTML: (attrs) => ({ 'data-merge-token': attrs.token }),
      },
      label: {
        default: '',
        parseHTML: (el) => el.textContent ?? '',
        renderHTML: () => ({}), // label is rendered as text content, not an attribute
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-merge-token]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class:
          'merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ' +
          'bg-primary/10 text-primary border border-primary/20 ' +
          'select-none cursor-default mx-0.5',
        contenteditable: 'false',
      }),
      node.attrs.label as string,
    ];
  },

  renderText({ node }) {
    // When exporting as plain text, use the friendly label
    return node.attrs.label as string;
  },
});

// ============================================================================
// [ trigger suggestion extension
// Watches for '[' keystrokes and tracks the query string until the user
// picks a token, presses Escape, or types a space/Enter without matching.
// ============================================================================

interface SuggestionState {
  active: boolean;
  query: string;
  range: { from: number; to: number } | null;
  rect: DOMRect | null;
}

const DEFAULT_SUGGESTION: SuggestionState = {
  active: false,
  query: '',
  range: null,
  rect: null,
};

// ============================================================================
// Floating suggestion dropdown
// ============================================================================

interface SuggestionDropdownProps {
  tokens: MergeToken[];
  query: string;
  rect: DOMRect;
  selectedIndex: number;
  onSelect: (token: MergeToken) => void;
}

const SuggestionDropdown = ({
  tokens,
  query,
  rect,
  selectedIndex,
  onSelect,
}: SuggestionDropdownProps) => {
  const filtered = React.useMemo(() => {
    const q = query.toLowerCase();
    return tokens.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.token.toLowerCase().includes(q) ||
        (t.description?.toLowerCase().includes(q) ?? false),
    );
  }, [tokens, query]);

  if (filtered.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed z-[9999] w-64 rounded-md border border-border bg-popover shadow-lg overflow-hidden"
      style={{
        top: rect.bottom + 4 + window.scrollY,
        left: rect.left + window.scrollX,
      }}
    >
      <div className="px-2 py-1.5 border-b border-border bg-muted/50">
        <span className="text-xs text-muted-foreground font-medium">
          {query ? `Matching "${query}"` : 'Type to filter variables…'}
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto py-1">
        {filtered.map((token, i) => (
          <button
            key={token.token}
            type="button"
            className={cn(
              'w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-accent',
              i === selectedIndex && 'bg-accent',
            )}
            onMouseDown={(e) => {
              e.preventDefault(); // keep editor focus
              onSelect(token);
            }}
          >
            <span className="text-sm font-medium leading-tight">
              {token.label.replace(/^\[|\]$/g, '')}
            </span>
            {token.description && (
              <span className="text-xs text-muted-foreground leading-tight">
                {token.description}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="px-2 py-1 border-t border-border bg-muted/30">
        <span className="text-[10px] text-muted-foreground">
          ↑↓ navigate · Enter select · Esc dismiss
        </span>
      </div>
    </div>,
    document.body,
  );
};

// ============================================================================
// Toolbar button (matches the style used in RichTextEditor)
// ============================================================================

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  className?: string;
}

const ToolbarButton = ({
  onClick,
  isActive,
  disabled,
  title,
  children,
  className,
}: ToolbarButtonProps) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'h-7 w-7 p-0 rounded',
      isActive
        ? 'bg-primary/10 text-primary hover:bg-primary/15'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent',
      className,
    )}
  >
    {children}
  </Button>
);

// ============================================================================
// Category display config
// ============================================================================

const CATEGORY_LABELS: Record<MergeToken['category'], string> = {
  client: 'Client',
  invoice: 'Invoice',
  project: 'Project',
  company: 'Company',
};

// Fixed display order for categories in the dropdown
const CATEGORY_ORDER: MergeToken['category'][] = ['client', 'invoice', 'project', 'company'];

// ============================================================================
// MergeFieldEditor props
// ============================================================================

export interface MergeFieldEditorProps {
  /** HTML string content (may contain merge chip spans) */
  content: string;
  /** Called with updated HTML on every change */
  onChange: (html: string) => void;
  /**
   * The tokens available in the "Insert field ▼" dropdown.
   * Accepts the full `MergeToken[]` from `getMergeTokens()` or a subset.
   * Sub-task 4.1f.
   */
  availableTokens: MergeToken[];
  /** Maximum character count */
  maxLength?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class names for the outer wrapper */
  className?: string;
  /** When true the editor is read-only */
  disabled?: boolean;
}

// ============================================================================
// MergeFieldEditor component
// ============================================================================

const MergeFieldEditor = React.forwardRef<HTMLDivElement, MergeFieldEditorProps>(
  (
    { content, onChange, availableTokens, maxLength, placeholder, className, disabled },
    ref,
  ) => {
    // ── Suggestion state ───────────────────────────────────────────────────
    const [suggestion, setSuggestion] = React.useState<SuggestionState>(DEFAULT_SUGGESTION);
    const [suggestionSelectedIndex, setSuggestionSelectedIndex] = React.useState(0);
    const suggestionRef = React.useRef<SuggestionState>(DEFAULT_SUGGESTION);
    suggestionRef.current = suggestion;

    // ── [ trigger Tiptap Extension ─────────────────────────────────────────
    const TriggerExtension = React.useMemo(
      () =>
        Extension.create({
          name: 'mergeTrigger',
          addKeyboardShortcuts() {
            return {
              '[': () => {
                const { state } = this.editor;
                const { from } = state.selection;
                // Schedule state update after Tiptap processes the keystroke
                setTimeout(() => {
                  const sel = window.getSelection();
                  const rect = sel && sel.rangeCount > 0
                    ? sel.getRangeAt(0).getBoundingClientRect()
                    : null;
                  setSuggestion({
                    active: true,
                    query: '',
                    range: { from, to: from + 1 },
                    rect,
                  });
                  setSuggestionSelectedIndex(0);
                }, 0);
                return false; // let the '[' character through
              },
              Escape: () => {
                if (suggestionRef.current.active) {
                  setSuggestion(DEFAULT_SUGGESTION);
                  return true;
                }
                return false;
              },
              ArrowDown: () => {
                if (!suggestionRef.current.active) return false;
                setSuggestionSelectedIndex((i) => i + 1);
                return true;
              },
              ArrowUp: () => {
                if (!suggestionRef.current.active) return false;
                setSuggestionSelectedIndex((i) => Math.max(0, i - 1));
                return true;
              },
              Enter: () => {
                // Handled via ref in the onUpdate callback
                return false;
              },
            };
          },
        }),
      [],
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          hardBreak: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            class: 'text-info-500 underline cursor-pointer',
          },
        }),
        MergeChipNode,
        TriggerExtension,
        ...(maxLength !== undefined
          ? [CharacterCount.configure({ limit: maxLength })]
          : [CharacterCount]),
      ],
      content,
      editable: !disabled,
      onUpdate({ editor: e }) {
        const html = e.isEmpty ? '' : e.getHTML();
        onChange(html);

        // Track query text after '[' trigger
        const curr = suggestionRef.current;
        if (curr.active && curr.range) {
          const { from } = e.state.selection;
          const triggerStart = curr.range.from;
          if (from > triggerStart) {
            // Extract text typed after '['
            const text = e.state.doc.textBetween(triggerStart, from, ' ');
            // text starts with '[' — query is everything after it
            const query = text.startsWith('[') ? text.slice(1) : text;
            // If user typed a space — dismiss
            if (query.includes(' ')) {
              setSuggestion(DEFAULT_SUGGESTION);
            } else {
              // Update rect to follow cursor
              const sel = window.getSelection();
              const rect = sel && sel.rangeCount > 0
                ? sel.getRangeAt(0).getBoundingClientRect()
                : curr.rect;
              setSuggestion((s) => ({ ...s, query, range: { from: triggerStart, to: from }, rect: rect ?? s.rect }));
              setSuggestionSelectedIndex(0);
            }
          } else {
            // Cursor moved before trigger — dismiss
            setSuggestion(DEFAULT_SUGGESTION);
          }
        }
      },
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none',
            'min-h-[80px] px-3 py-2 text-sm outline-none focus:outline-none',
          ),
          ...(placeholder ? { 'data-placeholder': placeholder } : {}),
        },
      },
    });

    // Sync external content changes
    React.useEffect(() => {
      if (!editor) return;
      const current = editor.isEmpty ? '' : editor.getHTML();
      if (content !== current) {
        editor.commands.setContent(content ?? '', false);
      }
    }, [content, editor]);

    // Sync disabled prop
    React.useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    // --- Link helper ---
    const handleSetLink = React.useCallback(() => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href as string | undefined;
      const url = window.prompt('Enter URL', previousUrl ?? 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    // --- Insert merge chip (from toolbar dropdown or [ trigger) ---
    const insertToken = React.useCallback(
      (mergeToken: MergeToken, fromTrigger = false) => {
        if (!editor) return;
        const curr = suggestionRef.current;

        if (fromTrigger && curr.active && curr.range) {
          // Delete the '[query' text then insert the chip
          editor
            .chain()
            .focus()
            .deleteRange({ from: curr.range.from, to: curr.range.to })
            .insertContent({
              type: 'mergeChip',
              attrs: { token: mergeToken.token, label: mergeToken.label },
            })
            .run();
        } else {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'mergeChip',
              attrs: { token: mergeToken.token, label: mergeToken.label },
            })
            .run();
        }
        setSuggestion(DEFAULT_SUGGESTION);
      },
      [editor],
    );

    // --- Handle Enter key for suggestion selection ---
    const filteredSuggestionTokens = React.useMemo(() => {
      if (!suggestion.active) return [];
      const q = suggestion.query.toLowerCase();
      return availableTokens.filter(
        (t) =>
          t.label.toLowerCase().includes(q) ||
          t.token.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q) ?? false),
      );
    }, [suggestion.active, suggestion.query, availableTokens]);

    React.useEffect(() => {
      if (!suggestion.active || !editor) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && filteredSuggestionTokens.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          const token = filteredSuggestionTokens[Math.min(suggestionSelectedIndex, filteredSuggestionTokens.length - 1)];
          if (token) insertToken(token, true);
        }
      };
      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestion.active, filteredSuggestionTokens, suggestionSelectedIndex, insertToken, editor]);

    // Group tokens by category, respecting CATEGORY_ORDER
    const tokensByCategory = React.useMemo(() => {
      const grouped = new Map<MergeToken['category'], MergeToken[]>();
      for (const cat of CATEGORY_ORDER) {
        const tokens = availableTokens.filter((t) => t.category === cat);
        if (tokens.length > 0) {
          grouped.set(cat, tokens);
        }
      }
      // Any remaining categories not in CATEGORY_ORDER
      for (const token of availableTokens) {
        if (!CATEGORY_ORDER.includes(token.category)) {
          const existing = grouped.get(token.category) ?? [];
          grouped.set(token.category, [...existing, token]);
        }
      }
      return grouped;
    }, [availableTokens]);

    // Character count
    const charCount = editor?.storage.characterCount?.characters?.() ?? 0;
    const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.9;
    const isAtLimit = maxLength !== undefined && charCount >= maxLength;

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-md border border-input bg-background shadow-xs',
          'focus-within:border-ring focus-within:ring-ring focus-within:ring-[3px]',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        {/* ── Toolbar ──────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1">
          <ToolbarButton
            title="Bold (Ctrl+B)"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            isActive={editor?.isActive('bold')}
            disabled={!editor}
          >
            <Bold className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Italic (Ctrl+I)"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            isActive={editor?.isActive('italic')}
            disabled={!editor}
          >
            <Italic className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Underline (Ctrl+U)"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            isActive={editor?.isActive('underline')}
            disabled={!editor}
          >
            <UnderlineIcon className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Bullet List"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            isActive={editor?.isActive('bulletList')}
            disabled={!editor}
          >
            <List className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Numbered List"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            isActive={editor?.isActive('orderedList')}
            disabled={!editor}
          >
            <ListOrdered className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Insert / Edit Link"
            onClick={handleSetLink}
            isActive={editor?.isActive('link')}
            disabled={!editor}
          >
            <LinkIcon className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Clear Formatting"
            onClick={() =>
              editor?.chain().focus().clearNodes().unsetAllMarks().run()
            }
            disabled={!editor}
          >
            <RemoveFormatting className="size-3.5" />
          </ToolbarButton>

          {/* ── Insert field dropdown ─────────────────────────── */}
          {availableTokens.length > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!editor}
                    className={cn(
                      'h-7 gap-1 rounded px-2 text-xs font-normal',
                      'text-muted-foreground hover:text-foreground hover:bg-accent',
                    )}
                  >
                    <Tag className="size-3.5" />
                    Insert field
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  className="w-56 max-h-80 overflow-y-auto"
                  // Prevent the editor from losing focus when the dropdown opens
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {Array.from(tokensByCategory.entries()).map(
                    ([category, tokens], groupIndex) => (
                      <React.Fragment key={category}>
                        {groupIndex > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                          {CATEGORY_LABELS[category] ?? category}
                        </DropdownMenuLabel>
                        {tokens.map((mergeToken) => (
                          <DropdownMenuItem
                            key={mergeToken.token}
                            onSelect={() => insertToken(mergeToken)}
                            className="flex flex-col items-start gap-0.5 cursor-pointer"
                          >
                            <span className="text-sm font-medium">
                              {/* Strip the [ ] brackets from the label for a cleaner display */}
                              {mergeToken.label.replace(/^\[|\]$/g, '')}
                            </span>
                            {mergeToken.description && (
                              <span className="text-xs text-muted-foreground leading-tight">
                                {mergeToken.description}
                              </span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </React.Fragment>
                    ),
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>

        {/* ── Editor area ──────────────────────────────────────── */}
        <EditorContent editor={editor} />

        {/* ── Character count ──────────────────────────────────── */}
        {maxLength !== undefined && (
          <div className="flex justify-end border-t border-border px-3 py-1">
            <span
              className={cn(
                'text-xs tabular-nums',
                isAtLimit
                  ? 'text-destructive'
                  : isNearLimit
                    ? 'text-warning-600 dark:text-warning-500'
                    : 'text-muted-foreground',
              )}
            >
              {charCount}/{maxLength}
            </span>
          </div>
        )}

        {/* ── [ trigger suggestion dropdown (portal) ───────────── */}
        {suggestion.active && suggestion.rect && (
          <SuggestionDropdown
            tokens={availableTokens}
            query={suggestion.query}
            rect={suggestion.rect}
            selectedIndex={suggestionSelectedIndex}
            onSelect={(token) => insertToken(token, true)}
          />
        )}
      </div>
    );
  },
);

MergeFieldEditor.displayName = 'MergeFieldEditor';

export { MergeFieldEditor };
